import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiveChatMessage, LiveChatService, LiveChatSession, LiveChatTransferUser } from '../../../core/services/live-chat.service';
import { TokenStorageService } from '../../../core/services/token-storage.service';

@Component({
  selector: 'app-csr-live-messages',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './csr-live-messages.component.html',
  styleUrls: ['./csr-live-messages.component.scss'],
})
export class CsrLiveMessagesComponent implements OnInit, OnDestroy {
  sessions: LiveChatSession[] = [];
  messages: LiveChatMessage[] = [];
  selected: LiveChatSession | null = null;
  reply = '';
  loading = false;
  sending = false;
  toast = '';
  query = '';
  viewMode: 'active' | 'history' = 'active';
  transferOpen = false;
  transferTargetUserId: number | null = null;
  transferUsers: LiveChatTransferUser[] = [];
  transferLoading = false;
  transferring = false;
  private sub?: Subscription;
  private toastTimer: any = null;
  private messageIds = new Set<number>();

  get filteredSessions() {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.sessions;
    return this.sessions.filter(s =>
      [s.citizenName, s.citizenMobile, s.sourceQuestion, s.status]
        .some(v => String(v ?? '').toLowerCase().includes(q))
    );
  }
  get canReply() { return this.viewMode === 'active' && this.selected?.status === 'ACTIVE' && !this.sending; }
  get availableTransferUsers() {
    const currentUserId = Number(this.tokenStorage.getUserId());
    const assignedUserId = Number(this.selected?.assignedAgentUserId);
    return this.transferUsers.filter(u => Number(u.userId) !== currentUserId && Number(u.userId) !== assignedUserId);
  }

  constructor(
    private liveChat: LiveChatService,
    private router: Router,
    private tokenStorage: TokenStorageService,
  ) {}

  ngOnInit() {
    this.viewMode = this.router.url.includes('/history') ? 'history' : 'active';
    this.loadSessions();
    this.liveChat.connect();
    this.sub = this.liveChat.events$.subscribe(event => {
      if (event.session) this.upsertSession(event.session);
      if (event.type === 'SESSION_TRANSFERRED' && event.session) this.handleTransferEvent(event.session);
      if (event.message && this.selected?.id === event.message.sessionId) {
        this.appendMessage(event.message);
        this.refreshSelectedMessages();
      }
      if (event.type === 'SESSION_STARTED') this.showToast('New live message assigned');
      if (event.type === 'SESSION_CLOSED' && event.session?.id === this.selected?.id) {
        this.selected = event.session ?? null;
        this.showToast('Chat closed');
      }
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  loadSessions() {
    this.loading = true;
    const request = this.viewMode === 'history'
      ? this.liveChat.assignedHistory()
      : this.liveChat.assignedSessions();
    request.subscribe({
      next: res => {
        this.sessions = this.liveChat.unwrapList<LiveChatSession>(res);
        if (!this.selected && this.sessions.length) this.selectSession(this.sessions[0]);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showToast('Unable to load live messages');
      }
    });
  }

  setViewMode(mode: 'active' | 'history') {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.selected = null;
    this.messages = [];
    this.messageIds.clear();
    this.loadSessions();
  }

  selectSession(session: LiveChatSession) {
    this.selected = session;
    this.messages = [];
    this.messageIds.clear();
    this.liveChat.messages(session.id).subscribe({
      next: res => this.replaceMessages(this.liveChat.unwrapList<LiveChatMessage>(res)),
      error: () => this.showToast('Unable to load conversation')
    });
  }

  sendReply() {
    const text = this.reply.trim();
    if (!text || !this.selected || !this.canReply) return;
    this.sending = true;
    this.liveChat.sendMessage(this.selected.id, text).subscribe({
      next: res => {
        const msg = this.liveChat.unwrap<LiveChatMessage>(res);
        this.appendMessage(msg);
        this.refreshSelectedMessages();
        this.reply = '';
        this.sending = false;
      },
      error: () => {
        this.sending = false;
        this.showToast('Reply failed');
      }
    });
  }

  closeChat() {
    if (!this.selected || this.selected.status === 'CLOSED') return;
    this.liveChat.close(this.selected.id).subscribe({
      next: res => {
        const updated = this.liveChat.unwrap<LiveChatSession>(res);
        this.upsertSession(updated);
        this.selected = updated;
        this.showToast('Chat closed');
      },
      error: () => this.showToast('Unable to close chat')
    });
  }

  openTransfer() {
    if (!this.selected || this.selected.status === 'CLOSED') return;
    this.transferOpen = !this.transferOpen;
    if (this.transferOpen && this.transferUsers.length === 0) this.loadTransferUsers();
  }

  loadTransferUsers() {
    this.transferLoading = true;
    this.liveChat.internalUsers().subscribe({
      next: res => {
        this.transferUsers = this.liveChat.unwrapList<LiveChatTransferUser>(res);
        this.transferLoading = false;
      },
      error: () => {
        this.transferLoading = false;
        this.showToast('Unable to load agents');
      }
    });
  }

  transferChat() {
    if (!this.selected || !this.transferTargetUserId || this.transferring) return;
    this.transferring = true;
    this.liveChat.transfer(this.selected.id, Number(this.transferTargetUserId)).subscribe({
      next: res => {
        const updated = this.liveChat.unwrap<LiveChatSession>(res);
        this.handleTransferEvent(updated);
        this.transferOpen = false;
        this.transferTargetUserId = null;
        this.transferring = false;
        this.showToast('Chat transferred');
      },
      error: () => {
        this.transferring = false;
        this.showToast('Unable to transfer chat');
      }
    });
  }

  goToConsole() { void this.router.navigate(['/telephony']); }

  formatTime(value?: string) {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  private upsertSession(session: LiveChatSession) {
    const idx = this.sessions.findIndex(s => s.id === session.id);
    if (idx >= 0) this.sessions[idx] = session;
    else this.sessions.unshift(session);
    this.sessions = [...this.sessions].sort((a, b) =>
      new Date(b.lastMessageAt || b.createdAt || 0).getTime() - new Date(a.lastMessageAt || a.createdAt || 0).getTime()
    );
  }

  private handleTransferEvent(session: LiveChatSession) {
    const currentUserId = Number(this.tokenStorage.getUserId());
    const stillAssignedToMe = Number(session.assignedAgentUserId) === currentUserId;
    if (!stillAssignedToMe && this.viewMode === 'active') {
      this.sessions = this.sessions.filter(s => s.id !== session.id);
      if (this.selected?.id === session.id) {
        this.selected = null;
        this.messages = [];
        this.messageIds.clear();
        this.transferOpen = false;
      }
      return;
    }
    this.upsertSession(session);
    if (this.selected?.id === session.id) this.selected = session;
  }

  private refreshSelectedMessages() {
    if (!this.selected) return;
    this.liveChat.messages(this.selected.id).subscribe({
      next: res => this.replaceMessages(this.liveChat.unwrapList<LiveChatMessage>(res)),
      error: () => {}
    });
  }

  private replaceMessages(messages: LiveChatMessage[]) {
    this.messageIds.clear();
    this.messages = [];
    messages.forEach(message => this.appendMessage(message));
  }

  private appendMessage(message: LiveChatMessage) {
    if (!message?.messageText) return;
    if (message.id && this.messageIds.has(message.id)) return;
    if (message.id) this.messageIds.add(message.id);
    this.messages = [...this.messages, message];
  }

  private showToast(message: string) {
    this.toast = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast = '', 2500);
  }
}
